import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';

// 语言数据
const translations = {
  zh: {
    nav: {
      home: '首页',
      about: '关于我',
      experience: '经历',
      skills: '技能',
      games: '游戏体验',
      portfolio: '作品集',
      gallery: '图库',
      contact: '联系',
      contactMe: '联系我',
    },
    home: {
      tagline: '专注技术型关卡 · UE / C++ / 数据驱动',
      greeting: '你好，我是',
      intro: 'UNSW 信息技术硕毕业，擅长用程序化思维打磨关卡节奏与玩家体验。喜欢把战斗逻辑、AI 行为和数据可视化结合起来，让每一次迭代都能被量化、可追踪。',
      contactBtn: '联系我',
      viewWorkBtn: '查看作品',
      stats: [
        { label: '项目实践', value: '6+', desc: '课程/团队交付与独立 Demo' },
        { label: '关卡迭代', value: '20+', desc: '可玩性验证与数值打磨' },
        { label: '技术栈', value: 'C++ / UE / React', desc: '工坊、蓝图与前端交互' },
      ],
    },
    about: {
      title: '关于我',
      desc1: '我在 UNSW 攻读信息技术硕士，专注于游戏开发、计算机视觉与机器学习。在课程与团队项目中，我习惯用数据与工具驱动迭代——把武器参数、AI 行为、关卡节奏转成可调的配置，快速验证「好玩」的具体指标。',
      desc2: '独立完成 FPS Demo《光蚀》，从 C++ 核心逻辑到蓝图动画状态机再到美术替换，都以可复用模块为目标。同时在 CNS 教育平台项目中，用 React 与 Strapi 交付可视化交互，确保内容对不同文化背景的用户友好。',
      contactTitle: '联系 / 快速响应',
      availability: '作为应届生，正在积极寻找实习或全职机会，期待能和您聊聊技术实现、系统设计或前端交互，向您多多学习。',
      infoCards: [
        { label: '姓名', value: '马君羽' },
        { label: '目标岗位', value: '技术型关卡设计实习生' },
        { label: '毕业时间', value: '2026 年 01 月 29 日' },
        { label: '技术标签', value: 'C++ / UE5 / 蓝图 / React / 数据分析' },
      ],
    },
    experience: {
      title: '经历',
      badge: '项目 · 教育 · 交付',
    },
    games: {
      title: '游戏体验',
      subtitle: '常玩与深度体验的游戏，支撑我对节奏与手感的理解',
      badge: '玩家视角 · 体验拆解',
      playRecord: '游玩记录',
      prevGame: '上一组游戏',
      nextGame: '下一组游戏',
    },
    skills: {
      title: '技能树',
      otherSkills: '其他技能',
    },
    portfolio: {
      title: '作品集',
      viewDetails: '查看详情',
    },
    gallery: {
      title: '图库与视频',
      video: '视频',
      image: '图片',
      clickToEnlarge: '点击放大',
    },
    contact: {
      title: '联系我',
      desc: '如果您正在寻找具备技术背景的关卡设计人才，或对游戏开发、系统实现、交互体验等方向有合作机会，欢迎邮件联系。我正在积极寻找实习或全职岗位，期待与您交流，24小时内回复。',
      leaveMessage: '留言给我',
      yourName: '您的姓名',
      yourEmail: '您的邮箱',
      yourMessage: '您的留言',
      sending: '发送中...',
      sendMessage: '发送消息',
      successMessage: '留言已发送！我会尽快回复您。',
      errorMessage: '发送失败：{error}。请直接发邮件至 junyu.ma.resume@gmail.com',
    },
    footer: {
      copyright: '保留所有权利',
      tags: ['关卡设计', '数据化迭代'],
    },
    lightbox: {
      close: '关闭',
    },
  },
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      experience: 'Experience',
      skills: 'Skills',
      games: 'Gaming',
      portfolio: 'Portfolio',
      gallery: 'Gallery',
      contact: 'Contact',
      contactMe: 'Contact Me',
    },
    home: {
      tagline: 'Technical Level Design · UE / C++ / Data-Driven',
      greeting: 'Hello, I\'m',
      intro: 'Master of IT graduate from UNSW, specializing in procedural approaches to level design and player experience. I combine combat logic, AI behavior, and data visualization to make every iteration quantifiable and trackable.',
      contactBtn: 'Contact Me',
      viewWorkBtn: 'View Work',
      stats: [
        { label: 'Projects', value: '6+', desc: 'Course/Team Deliverables & Independent Demos' },
        { label: 'Level Iterations', value: '20+', desc: 'Playability Testing & Balance Tuning' },
        { label: 'Tech Stack', value: 'C++ / UE / React', desc: 'Workshop, Blueprints & Frontend' },
      ],
    },
    about: {
      title: 'About Me',
      desc1: 'I\'m pursuing a Master of Information Technology at UNSW, focusing on game development, computer vision, and machine learning. In coursework and team projects, I use data and tools to drive iteration—turning weapon parameters, AI behavior, and level pacing into configurable settings to quickly validate what makes gameplay "fun."',
      desc2: 'Independently completed the FPS demo "Light Erosion," building everything from C++ core logic to Blueprint animation state machines to art replacement, all with reusable modules in mind. Also worked on the CNS education platform project, delivering visual interactions with React and Strapi to ensure content is friendly to users from different cultural backgrounds.',
      contactTitle: 'Contact / Quick Response',
      availability: 'As a recent graduate, I\'m actively seeking internship or full-time opportunities. I look forward to discussing technical implementation, system design, or frontend interactions with you and learning from your expertise.',
      infoCards: [
        { label: 'Name', value: 'Junyu Ma' },
        { label: 'Target Role', value: 'Technical Level Design Intern' },
        { label: 'Graduation', value: 'January 29, 2026' },
        { label: 'Tech Tags', value: 'C++ / UE5 / Blueprints / React / Data Analysis' },
      ],
    },
    experience: {
      title: 'Experience',
      badge: 'Projects · Education · Delivery',
    },
    games: {
      title: 'Gaming Experience',
      subtitle: 'Games I play and deeply understand, supporting my insights into pacing and feel',
      badge: 'Player Perspective · Experience Analysis',
      playRecord: 'Play Record',
      prevGame: 'Previous Game',
      nextGame: 'Next Game',
    },
    skills: {
      title: 'Skills',
      otherSkills: 'Other Skills',
    },
    portfolio: {
      title: 'Portfolio',
      viewDetails: 'View Details',
    },
    gallery: {
      title: 'Gallery & Videos',
      video: 'Video',
      image: 'Image',
      clickToEnlarge: 'Click to Enlarge',
    },
    contact: {
      title: 'Contact Me',
      desc: 'If you\'re looking for level design talent with a technical background, or have collaboration opportunities in game development, system implementation, or interaction design, please feel free to reach out via email. I\'m actively seeking internship or full-time positions and look forward to connecting with you. I respond within 24 hours.',
      leaveMessage: 'Leave a Message',
      yourName: 'Your Name',
      yourEmail: 'Your Email',
      yourMessage: 'Your Message',
      sending: 'Sending...',
      sendMessage: 'Send Message',
      successMessage: 'Message sent! I\'ll reply as soon as possible.',
      errorMessage: 'Failed to send: {error}. Please email directly to junyu.ma.resume@gmail.com',
    },
    footer: {
      copyright: 'All Rights Reserved',
      tags: ['Level Design', 'Data-Driven Iteration'],
    },
    lightbox: {
      close: 'Close',
    },
  },
};

// 获取翻译数据的辅助函数
const getExperiences = (lang) => {
  if (lang === 'en') {
    return [
      {
        date: '2025.09 - 2025.11',
        title: 'Independent Game Development · Light Erosion Demo',
        subtitle: 'UNSW Course Project',
        bullets: [
          'Modular weapon and projectile system with AI behavior trees, enabling minute-level combat pacing adjustments.',
          'Innovative dual-layer collision capsule system that completely solves AI clipping issues, significantly improving level fluidity.',
          'Project achieved HD score of 91.3/100, combining performance, gameplay, and engineering maturity.',
        ],
      },
      {
        date: '2024.02 - 2025.12 (Expected)',
        title: 'Master of Information Technology',
        subtitle: 'University of New South Wales (UNSW)',
        bullets: [
          'Core: Computer Graphics / Neural Networks / Vision / Machine Learning / Databases',
          'Practice: RadianceAscending (3D Roguelike FPS) technical level design; CNS education platform frontend interaction and system upgrade',
        ],
      },
      {
        date: '2023.10 - 2024.01',
        title: 'English Teaching Assistant',
        subtitle: 'Shangxin International Education',
        bullets: [
          '60+ hours of specialized oral coaching, optimizing pronunciation, grammar, and test strategies',
          'Python + Excel automation for 200+ weekly reports, quantitatively tracking learning progress',
        ],
      },
      {
        date: '2021.07 - Present',
        title: 'Network Equipment Assembly Technician',
        subtitle: 'Xuefu Graduate School',
        bullets: [
          'Diagnosed legacy network (1 switch + 4 routers) for 80 students/≈160 terminals, found single router stable limit ≈10 devices, high-concurrency video stutter rate >40%, Apple devices frequently disconnected due to channel conflicts',
          'Designed and implemented solution: 2 AX6000 + Gigabit switch mesh network, seamless roaming + QoS rate limiting; used NetSpot to scan blind spots/interference, adjusted channels to low-conflict bands',
          'Results: 100% coverage (edge signal improved from <-80dBm to ≥-65dBm), 20-device simultaneous HD video stutter rate 40%→0%, Apple disconnection rate 50%→5%',
        ],
      },
      {
        date: '2018.09 - 2022.06',
        title: 'Computer Science and Technology',
        subtitle: 'Guangdong Polytechnic Normal University',
        bullets: [
          'Thesis: WeChat Mini Program canteen ordering platform (frontend + backend + database design)',
          'Related courses: Data Structures, JAVA Programming, Software Engineering, Computer Networks, Computer Organization',
        ],
      },
    ];
  }
  return [
    {
      date: '2025.09 - 2025.11',
      title: '独立游戏开发 · 光蚀 Demo',
      subtitle: 'UNSW 课程项目',
      bullets: [
        '模块化武器与抛射物系统，配合AI行为树，实现战斗节奏分钟级调优。',
        '创新采用双层碰撞胶囊体，彻底解决AI穿模问题，大幅提升关卡流畅度。',
        '项目实测获HD综合评分91.3/100，兼具性能、玩法与工程成熟度。',
      ],
    },
    {
      date: '2024.02 - 2025.12（预期）',
      title: 'Master of Information Technology',
      subtitle: '新南威尔士大学（UNSW）',
      bullets: [
        '核心：计算机图像 / 神经网络 / 视觉 / 机器学习 / 数据库',
        '实践：RadianceAscending(3D 肉鸽 FPS) 技术型关卡设计；CNS 教育平台前端交互与系统升级',
      ],
    },
    {
      date: '2023.10 - 2024.01',
      title: '英语助教',
      subtitle: '上新国际教育',
      bullets: [
        '60+ 小时口语专项辅导，优化发音、语法与应试策略',
        'Python + Excel 自动化 200+ 份周报，量化跟踪学习进度',
      ],
    },
    {
      date: '2021.07 - 至今',
      title: '网络设备装配技术员',
      subtitle: '学府考研',
      bullets: [
        '面向 80 名学生/≈160 台终端诊断旧网（1 交换机+4 路由），发现单路由稳定上限≈10 台，高并发视频卡顿率 >40%，苹果设备因信道冲突频繁掉线',
        '设计并落地方案：2 台 AX6000 + 千兆交换机组网，Mesh 无缝漫游 + QoS 限速均衡；用 NetSpot 全域扫盲区/干扰，调信道到低冲突频段',
        '结果：覆盖率 100%（边缘信号由 <-80dBm 提升至 ≥-65dBm），20 台终端同时高清视频卡顿率 40%→0%，苹果断连率 50%→5%',
      ],
    },
    {
      date: '2018.09 - 2022.06',
      title: '计算机科学与技术',
      subtitle: '广东技术师范大学',
      bullets: ['毕业论文：基于微信小程序的食堂订餐平台（前后端 + 数据库设计）','相关课程：数据结构、JAVA编程、软件工程、计算机网络、计算机组成原理'],
    },
  ];
};

const getSkills = (lang) => {
  if (lang === 'en') {
    return [
      { 
        name: 'C++', 
        value: 90,
        description: 'Implemented health system and basic weapon system in C++ for Light Erosion project, enabling rapid iteration through Blueprint subclass creation.'
      },
      { 
        name: 'Unreal Engine / Blueprints', 
        value: 86,
        description: 'Proficient in Blueprint system for AI behavior trees, animation state machines, and level logic. Solved clipping issues with dual-layer collision capsules, improving game fluidity. Exposed parameters via Blueprints for design balance and rapid iteration.'
      },
      { 
        name: 'Python / Data-Driven Development', 
        value: 88,
        description: 'Automated generation of 200+ reports using Python + Excel, with data processing, analysis, and visualization capabilities. Habitually use data to guide system balance.'
      },
      { 
        name: 'Gameplay Mechanics Understanding (Player → Engineering)', 
        value: 84,
        description: 'Top 0.01% CFM ranked player, long-term analysis of feel elements like TTK, input response, and skill interruption from a developer perspective, able to translate into configurable technical solutions.'
      },
      { 
        name: 'English & Engineering Literacy', 
        value: 80,
        description: 'PTE 69, fluent in reading English technical documentation. Familiar with Docker basics for deployment, proficient in Git for version control and team collaboration.'
      },
      { 
        name: 'Frontend & Tool Development (React)', 
        value: 78,
        description: 'Participated in education platform frontend upgrade, implemented interactive maps and forums with React + Strapi. Familiar with component-based development, potential for toolchain expansion.'
      },
    ];
  }
  return [
    { 
      name: 'C++', 
      value: 90,
      description: '在光蚀项目中用 C++ 实现了生命值系统以及基础的武器系统，方便后续使用蓝图创建子类快速迭代。'
    },
    { 
      name: 'Unreal Engine / 蓝图', 
      value: 86,
      description: '熟练使用蓝图系统实现 AI 行为树、动画状态机与关卡逻辑，通过双层碰撞胶囊体解决穿模问题，提升游戏流畅度。通过蓝图暴露参数以实现设计平衡与快速迭代。'
    },
    { 
      name: 'Python / 数据驱动开发', 
      value: 88,
      description: '使用 Python + Excel 自动化生成 200+ 份报告，具备数据处理、分析与可视化能力，习惯用数据指导系统平衡。'
    },
    { 
      name: '游戏机制理解（玩家 → 工程转化）', 
      value: 84,
      description: 'CFM 排位前 0.01% 玩家，长期从开发者视角拆解 TTK、输入响应、技能打断等手感要素，并能转化为可配置技术方案。'
    },
    { 
      name: 'English & 工程素养', 
      value: 80,
      description: 'PTE 69，可流畅阅读英文技术文档；了解 Docker 基础部署，熟练使用 Git 进行版本控制与团队协作。'
    },
    { 
      name: '前端与工具开发（React）', 
      value: 78,
      description: '参与教育平台前端升级，使用 React + Strapi 实现交互地图与论坛，熟悉组件化开发，具备工具链拓展潜力。'
    },
  ];
};

const getOtherSkills = (lang) => {
  if (lang === 'en') {
    return [
      'Data Visualization & Dashboards',
      '3D Modeling Basics',
      'Team Collaboration & Project Management',
      'Docker / Deployment Basics',
      'Scientific Fitness Management',
    ];
  }
  return [
    '数据可视化与仪表盘',
    '3D 建模基础',
    '团队协作与项目推进',
    'Docker / 部署基础',
    '科学健身管理',
  ];
};

const getProjects = (lang) => {
  if (lang === 'en') {
    return [
      {
        title: 'Light Erosion - FPS Demo',
        desc: 'C++ + UE implementation of weapon system, AI behavior trees, and combat pacing validation, including video and documentation delivery.',
        tags: ['C++', 'Unreal Engine', 'Blueprints', '3D Modeling'],
        image: 'images/project1.png',
        link: 'documents/光蚀项目文档.pdf',
      },
      {
        title: 'CNS Education Platform Upgrade',
        desc: 'React + Strapi frontend interaction and system optimization, culturally sensitive content visualization.',
        tags: ['React', 'Strapi', 'System Optimization', 'SVG Interaction'],
        image: 'images/cns home.jpg',
        link: 'documents/cns report.pdf',
      },
      {
        title: 'Mosquito Killer',
        desc: 'Android app based on Kotlin, using CameraX to access camera, with custom real-time image processing (color space transformation, dynamic threshold analysis) to identify and highlight small dark static objects in the frame, helping users find mosquitoes.',
        tags: ['Android','Kotlin','CameraX','Image Processing','Material Design'],
        image: 'images/mosquito killer.jpg',
        link: 'https://github.com/QperM/MosquitoKiller',
      },
    ];
  }
  return [
    {
      title: '光蚀 - FPS Demo',
      desc: 'C++ + UE 实现武器系统、AI 行为树与战斗节奏验证，含视频与文档交付。',
      tags: ['C++', 'Unreal Engine', '蓝图', '3D 建模'],
      image: 'images/project1.png',
      link: 'documents/光蚀项目文档.pdf',
    },
    {
      title: 'CNS 教育平台升级',
      desc: 'React + Strapi 前端交互与系统优化，文化敏感内容可视化呈现。',
      tags: ['React', 'Strapi', '系统优化', 'SVG 交互'],
      image: 'images/cns home.jpg',
      link: 'documents/cns report.pdf',
    },
    {
      title: 'Mosquito Killer',
      desc: '基于 Kotlin 的安卓应用，核心利用 CameraX 调用相机，并通过自定义实时图像处理（色彩空间变换、动态阈值分析），识别并高亮画面中的微小深色静态物体，辅助用户寻找蚊子',
      tags: ['Android','Kotlin','CameraX','图像处理','Material Design'],
      image: 'images/mosquito killer.jpg',
      link: 'https://github.com/QperM/MosquitoKiller',
    },
  ];
};

const getGames = (lang) => {
  if (lang === 'en') {
    return [
      {
        title: 'CrossFire: Mobile (CFM)',
        hours: 'Main Game',
        type: 'FPS · PVP/PVE',
        takeaways: 'Deep understanding of TTK (Time-to-Kill), shooting feedback, and combat pacing. Highly sensitive to multi-mode map layouts, numerical balance, and weapon feel. Long-term high-ranked player, deeply researched coupling relationships between mobile shooting feel, recoil feedback, skill cooldowns, and reload pacing. Ranked #83 globally in competitive ranked (12M DAU, top 0.01%), capable of reverse-engineering design logic from a player perspective.',
        image: '/images/games/cfm.jpg',
      },
      {
        title: 'Steam Single-Player',
        hours: '2000+ Hours',
        type: 'Action / RPG / Roguelike',
        takeaways: 'Focus on narrative pacing, combat loops, and numerical progression. Experienced Souls-like, roguelike, 4X strategy, and design-focused indie games. Personal game library includes 200+ titles. 100% completion of Black Myth: Wukong, Cyberpunk 2077, and more.',
        image: '/images/games/steam.jpg',
      },
      {
        title: 'League of Legends (LOL)',
        hours: '3000+ Matches',
        type: 'MOBA',
        takeaways: 'Veteran summoner since Season 4, 3000+ matches total, peak rank Platinum II. Over ten years, fully experienced rune reworks, item system iterations, and map ecosystem evolution. Habitually reverse-engineer mechanism design logic from player behavior—e.g., how minion wave interactions affect map mobility, how vision control shapes level pacing.',
        image: '/images/games/lol.jpg',
      },
      {
        title: 'Teamfight Tactics (TFT)',
        hours: '1200+ Games',
        type: 'Mobile / PC',
        takeaways: 'Reached Grandmaster rank 7 times, deeply adapted to multiple seasons of trait, economy, and item system iterations. In fast-paced version updates, continuously observed how tutorial guidance, team recommendations, and UI flow affect strategy learning cost and long-term retention.',
        image: '/images/games/金铲铲.jpg',
      }
    ];
  }
  return [
    {
      title: '穿越火线：枪战王者（CFM）',
      hours: '深度主游',
      type: 'FPS · PVP/PVE',
      takeaways: '深度理解 TTK（Time-to-Kill）、射击反馈与战斗节奏，对多模式地图布局、数值平衡及武器手感高度敏感。长期高段位玩家，深入研究移动射击手感、后坐力反馈、技能冷却与换弹节奏的耦合关系，在竞技排位中跻身全服第83名（1200万日活，约前0.01%），具备从玩家视角反推设计逻辑的能力。',
      image: '/images/games/cfm.jpg',
    },
    {
      title: 'Steam 平台单机',
      hours: '2000+ 小时',
      type: '动作 / RPG / Roguelike',
      takeaways: '关注叙事节奏、战斗循环与数值成长；体验过类魂系、肉鸽、4X策略、设计型独立游戏，自身游戏库涵盖200+款游戏。全成就通关黑神话悟空，赛博朋克2077等游戏。',
      image: '/images/games/steam.jpg',
    },
    {
      title: '英雄联盟（LOL）',
      hours: '3000+ 对局数',
      type: 'MOBA',
      takeaways: 'S4 赛季入坑的老召唤师，累计对局 3000+，最高段位铂金 II。十年间完整经历了符文重做、装备系统迭代与地图生态演变，习惯从玩家行为反推机制设计逻辑——比如兵线交互如何影响地图流动性，视野博弈怎样塑造关卡节奏。',
      image: '/images/games/lol.jpg',
    },
    {
      title: '金铲铲 & 云顶之弈',
      hours: '1200+ 局',
      type: '移动 / PC',
      takeaways: '7 次达到超凡大师段位，深度适应多个赛季的羁绊、经济与装备系统迭代。在快节奏版本更迭中，持续观察新手引导、阵容推荐与界面动线如何影响策略学习成本与长期留存。',
      image: '/images/games/金铲铲.jpg',
    }
  ];
};

const getMediaItems = (lang) => {
  if (lang === 'en') {
    return [
      {
        type: 'video',
        poster: 'images/cover/cover1.png',
        title: 'Light Erosion Demo',
        desc: 'Two-minute demo showcasing the first two levels of Light Erosion, experiencing the light energy survival mechanism and weapon system.',
        link: 'https://pan.baidu.com/s/1czqbzG9pYTs-RYMUT3xEcg?pwd=QWER',
      },
      {
        type: 'image',
        src: '/images/gallery/gallery1.png',
        title: 'Scene Screenshot 1',
        desc: 'Atmosphere & Lighting - World plunged into darkness, protagonist carries own light source',
      },
      {
        type: 'image',
        src: '/images/gallery/gallery2.png',
        title: 'Scene Screenshot 2',
        desc: 'Level Pacing Checkpoint - Teleportation array and teleportation effects',
      },
      {
        type: 'image',
        src: '/images/gallery/gallery3.png',
        title: 'Scene Screenshot 3',
        desc: 'Entrance Seal - Initially blocks player from entering the level',
      },
      {
        type: 'image',
        src: '/images/gallery/gallery4.png',
        title: 'Scene Screenshot 4',
        desc: 'Multi-layer UI',
      },
      {
        type: 'image',
        src: '/images/gallery/gallery5.png',
        title: 'Scene Screenshot 5',
        desc: 'Diverse Weapons - Different mechanics and feel',
      },
    ];
  }
  return [
    {
      type: 'video',
      poster: 'images/cover/cover1.png',
      title: '光蚀 Demo 演示',
      desc: '《光蚀》Demo两分钟展示前两关流程，体验光能生存机制与武器系统。',
      link: 'https://pan.baidu.com/s/1czqbzG9pYTs-RYMUT3xEcg?pwd=QWER',
    },
    {
      type: 'image',
      src: '/images/gallery/gallery1.png',
      title: '场景截图 1',
      desc: '氛围与光影 世界陷入黑暗 主角自带光源',
    },
    {
      type: 'image',
      src: '/images/gallery/gallery2.png',
      title: '场景截图 2',
      desc: '关卡节奏检查点 传送阵与传送特效',
    },
    {
      type: 'image',
      src: '/images/gallery/gallery3.png',
      title: '场景截图 3',
      desc: '入口封印 初始会阻挡玩家进入关卡',
    },
    {
      type: 'image',
      src: '/images/gallery/gallery4.png',
      title: '场景截图 4',
      desc: '多层级UI',
    },
    {
      type: 'image',
      src: '/images/gallery/gallery5.png',
      title: '场景截图 5',
      desc: '多样化武器 不同的机制与手感',
    },
  ];
};

const getContacts = (lang) => {
  if (lang === 'en') {
    return [
      { label: 'Email', value: 'junyu.ma.resume@gmail.com', href: 'mailto:junyu.ma.resume@gmail.com' },
      { label: 'Phone', value: '15535578110', href: 'tel:15535578110' },
      { label: 'Hometown', value: 'Changzhi, Shanxi' },
      { label: 'Education', value: 'Master\'s Degree (UNSW)' },
    ];
  }
  return [
    { label: '邮箱', value: 'junyu.ma.resume@gmail.com', href: 'mailto:junyu.ma.resume@gmail.com' },
    { label: '电话', value: '15535578110', href: 'tel:15535578110' },
    { label: '籍贯', value: '山西长治' },
    { label: '学历', value: '硕士研究生（UNSW）' },
  ];
};

function App() {
  const [language, setLanguage] = useState('zh'); // 'zh' or 'en'
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, src: '', type: 'image', title: '' });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const hasSwiped = useRef(false);
  const touchStartY = useRef(null);
  const touchEndY = useRef(null);

  const t = translations[language];
  const experiences = getExperiences(language);
  const skills = getSkills(language);
  const otherSkills = getOtherSkills(language);
  const projects = getProjects(language);
  const games = getGames(language);
  const mediaItems = getMediaItems(language);
  const contacts = getContacts(language);
  const [gameIndex, setGameIndex] = useState(() => Math.floor(games.length / 2));

  const navItems = [
    { label: t.nav.home, href: '#home' },
    { label: t.nav.about, href: '#about' },
    { label: t.nav.experience, href: '#experience' },
    { label: t.nav.skills, href: '#skills' },
    { label: t.nav.games, href: '#games' },
    { label: t.nav.portfolio, href: '#portfolio' },
    { label: t.nav.gallery, href: '#gallery' },
    { label: t.nav.contact, href: '#contact' },
  ];

  const handleScroll = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMenuOpen(false);
  };

  // 移动端滑动处理
  const minSwipeDistance = 50; // 最小滑动距离

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndY.current = null;
    hasSwiped.current = false;
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    touchEndY.current = e.targetTouches[0].clientY;
    // 如果检测到明显的垂直滑动，不阻止默认行为（允许页面滚动）
    if (touchStartY.current !== null) {
      const deltaY = Math.abs(e.targetTouches[0].clientY - touchStartY.current);
      const deltaX = Math.abs(e.targetTouches[0].clientX - (touchStart || 0));
      // 如果垂直滑动距离明显大于水平滑动，允许默认滚动行为
      if (deltaY > deltaX && deltaY > 10) {
        return; // 不阻止默认行为，允许页面滚动
      }
      // 如果是水平滑动，阻止默认行为以避免页面滚动
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || touchStartY.current === null || touchEndY.current === null) {
      hasSwiped.current = false;
      touchStartY.current = null;
      touchEndY.current = null;
      return;
    }
    
    const deltaX = touchStart - touchEnd;
    const deltaY = Math.abs(touchEndY.current - touchStartY.current);
    
    // 判断是水平滑动还是垂直滑动
    // 如果垂直滑动距离大于水平滑动距离，或者垂直滑动距离超过阈值，则认为是垂直滑动
    const isVerticalSwipe = deltaY > Math.abs(deltaX) && deltaY > 20;
    const isHorizontalSwipe = Math.abs(deltaX) > deltaY && Math.abs(deltaX) > minSwipeDistance;
    
    if (isHorizontalSwipe && !isVerticalSwipe) {
      const isLeftSwipe = deltaX > minSwipeDistance;
      const isRightSwipe = deltaX < -minSwipeDistance;

      if (isLeftSwipe) {
        // 向左滑动，显示下一张
        hasSwiped.current = true;
        setGameIndex((idx) => (idx + 1) % games.length);
      } else if (isRightSwipe) {
        // 向右滑动，显示上一张
        hasSwiped.current = true;
        setGameIndex((idx) => (idx - 1 + games.length) % games.length);
      }
    } else {
      hasSwiped.current = false;
    }
    
    // 重置触摸状态
    setTimeout(() => {
      setTouchStart(null);
      setTouchEnd(null);
      touchStartY.current = null;
      touchEndY.current = null;
      hasSwiped.current = false;
    }, 100);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });


    const templateParams = {
      from_name: e.target.name.value,
      from_email: e.target.email.value,
      message: e.target.message.value,
      reply_to: e.target.email.value, 
    };

    try {
      const response = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_uifo9q6',
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_stan71c',
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '11oS_enHDgk3u1Rrc'
      );

      console.log('EmailJS 响应:', response);
      setFormStatus({ 
        type: 'success', 
        message: t.contact.successMessage
      });
      e.target.reset();
    } catch (error) {
      console.error('发送失败详情:', error);
      console.error('错误代码:', error.code);
      console.error('错误消息:', error.text);
      setFormStatus({ 
        type: 'error', 
        message: t.contact.errorMessage.replace('{error}', error.text || error.message)
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="text-slate-900 bg-transparent">
      <header className="fixed inset-x-0 top-0 z-40">
        <div className="mx-auto flex max-w-6xl items-center justify-between rounded-2xl px-4 py-3 mt-3 glass shadow-lg">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-emerald-400 shadow-md" />
            <div>
              <p className="text-sm text-slate-500">{language === 'zh' ? '技术型关卡设计' : 'Technical Level Design'}</p>
              <p className="text-lg font-semibold text-slate-900">{language === 'zh' ? '马君羽' : 'Junyu Ma'}</p>
            </div>
          </div>
          <nav className="hidden items-center gap-4 text-sm font-medium text-slate-700 md:flex">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => handleScroll(item.href)}
                className="rounded-lg px-3 py-2 transition hover:text-indigo-600 hover:bg-indigo-50"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:text-indigo-600 hover:bg-indigo-50"
            >
              <span className={language === 'zh' ? 'font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent' : 'text-slate-700'}>
                中文
              </span>
              <span className="text-slate-400">/</span>
              <span className={language === 'en' ? 'font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent' : 'text-slate-700'}>
                English
              </span>
            </button>
            <button
              onClick={() => handleScroll('#contact')}
              className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-2 text-white shadow hover:shadow-lg transition"
            >
              {t.nav.contactMe}
            </button>
          </nav>
          <button
            className="md:hidden rounded-lg p-2 text-slate-700 hover:bg-slate-100"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="切换导航菜单"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        {menuOpen && (
          <div className="mx-4 mt-2 rounded-2xl bg-white shadow-lg md:hidden">
            <div className="flex flex-col divide-y divide-slate-100">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => handleScroll(item.href)}
                  className="px-4 py-3 text-left text-slate-700 hover:bg-indigo-50"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => setLanguage(language === 'zh' ? 'en' : 'zh')}
                className="flex items-center gap-1 px-4 py-3 text-left text-slate-700 hover:bg-indigo-50"
              >
                <span className={language === 'zh' ? 'font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent' : 'text-slate-700'}>
                  中文
                </span>
                <span className="text-slate-400">/</span>
                <span className={language === 'en' ? 'font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent' : 'text-slate-700'}>
                  English
                </span>
              </button>
              <button
                onClick={() => handleScroll('#contact')}
                className="px-4 py-3 text-left font-semibold text-indigo-600 hover:bg-indigo-50"
              >
                {t.nav.contactMe}
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="pt-28 md:pt-32">
        <section
          id="home"
          className="relative isolate overflow-hidden px-4 pb-20 pt-16 md:pt-24"
        >
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-indigo-50 via-white to-emerald-50" />
          <div className="mx-auto flex max-w-6xl flex-col gap-12 md:flex-row md:items-center">
            <div className="flex-1 space-y-6">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-indigo-600 shadow ring-1 ring-indigo-100">
                {t.home.tagline}
              </p>
              <h1 className="text-4xl font-bold leading-tight text-slate-900 md:text-5xl">
                {t.home.greeting}<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500"> {language === 'zh' ? '马君羽' : 'Junyu Ma'}</span>
              </h1>
              <p className="text-lg text-slate-600 md:text-xl">
                {t.home.intro}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleScroll('#contact')}
                  className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3 text-white shadow-lg shadow-indigo-200 transition hover:-translate-y-0.5 hover:shadow-xl"
                >
                  {t.home.contactBtn}
                </button>
                <button
                  onClick={() => handleScroll('#portfolio')}
                  className="rounded-full border border-slate-300 px-6 py-3 text-slate-800 transition hover:border-indigo-300 hover:text-indigo-600 hover:-translate-y-0.5"
                >
                  {t.home.viewWorkBtn}
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {t.home.stats.map((item) => (
                  <div
                    key={item.label}
                    className="glass rounded-xl p-4 shadow-sm ring-1 ring-white/60"
                  >
                    <p className="text-xs uppercase tracking-wide text-slate-500">{item.label}</p>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">{item.value}</p>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="relative mx-auto max-w-sm">
                <div className="absolute inset-0 -z-10 blur-3xl bg-indigo-200/50 rounded-full" />
                <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/80 shadow-xl shadow-indigo-200">
                  <img
                    src="/images/profile.jpg"
                    alt={language === 'zh' ? '马君羽' : 'Junyu Ma'}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute -left-10 -top-10 h-24 w-24 rounded-full bg-gradient-to-br from-emerald-400 via-cyan-400 to-indigo-500 opacity-60 blur-2xl" />
                <div className="absolute -right-10 -bottom-10 h-24 w-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 opacity-50 blur-2xl" />
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="px-4 py-16 md:py-20">
          <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-[1.2fr,0.8fr]">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-slate-900">{t.about.title}</h2>
              <p className="text-base leading-7 text-slate-600">
                {t.about.desc1}
              </p>
              <p className="text-base leading-7 text-slate-600">
                {t.about.desc2}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {t.about.infoCards.map((card) => (
                  <div key={card.label} className="glass rounded-xl p-4 shadow-sm">
                    <p className="text-sm text-indigo-600">{card.label}</p>
                    <p className="mt-1 text-base font-semibold text-slate-900">{card.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass h-fit space-y-4 rounded-2xl p-6 shadow-lg">
              <p className="text-sm font-semibold text-indigo-600">{t.about.contactTitle}</p>
              <div className="grid gap-3 text-sm text-slate-700">
                {contacts.map((item) => (
                  <div key={item.label} className="flex items-center justify-between rounded-lg bg-white/70 px-4 py-3">
                    <span className="font-medium text-slate-600">{item.label}</span>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="text-slate-900">{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 text-sm text-white shadow-md">
                {t.about.availability}
              </div>
            </div>
          </div>
        </section>

        <section id="experience" className="bg-white/70 px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-8">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-3xl font-bold text-slate-900">{t.experience.title}</h2>
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600">
                {t.experience.badge}
              </span>
            </div>
            <div className="relative space-y-8 before:absolute before:left-[10px] before:top-2 before:h-full before:w-0.5 before:bg-indigo-100 md:before:left-1/2 md:before:-translate-x-1/2">
              {experiences.map((exp, idx) => (
                <div
                  key={exp.title}
                  className={`relative md:w-1/2 ${idx % 2 === 0 ? 'md:ml-auto md:pl-10' : 'md:pr-10'}`}
                >
                  <div className="absolute left-0 top-2 h-4 w-4 rounded-full border-2 border-white bg-indigo-500 shadow md:left-auto md:right-[-22px]" />
                  <div className="glass rounded-2xl p-6 shadow-lg">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                      {exp.date}
                    </p>
                    <h3 className="mt-2 text-xl font-semibold text-slate-900">{exp.title}</h3>
                    <p className="text-sm text-slate-500">{exp.subtitle}</p>
                    <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
                      {exp.bullets.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-1 h-2 w-2 rounded-full bg-indigo-400" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="games" className="bg-white/70 px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-8">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-3xl font-bold text-slate-900">{t.games.title}</h2>
                <p className="text-sm text-slate-600">{t.games.subtitle}</p>
              </div>
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600">{t.games.badge}</span>
            </div>
            <div className="relative">
              <div className="absolute -left-4 top-1/2 z-20 hidden -translate-y-1/2 md:block">
                <button
                  onClick={() => setGameIndex((idx) => (idx - 1 + games.length) % games.length)}
                  className="rounded-full bg-white/90 p-3 shadow-lg ring-1 ring-slate-200 hover:-translate-x-0.5 hover:bg-white transition"
                  aria-label={t.games.prevGame}
                >
                  <svg className="h-5 w-5 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>
              <div className="absolute -right-4 top-1/2 z-20 hidden -translate-y-1/2 md:block">
                <button
                  onClick={() => setGameIndex((idx) => (idx + 1) % games.length)}
                  className="rounded-full bg-white/90 p-3 shadow-lg ring-1 ring-slate-200 hover:translate-x-0.5 hover:bg-white transition"
                  aria-label={t.games.nextGame}
                >
                  <svg className="h-5 w-5 text-slate-700" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <div 
                className="relative h-[480px] overflow-hidden py-8"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {games.map((game, idx) => {
                  const delta = (idx - gameIndex + games.length) % games.length;
                  const position = delta === 0 ? 'center' : delta === 1 ? 'right' : delta === games.length - 1 ? 'left' : 'hidden';
                  if (position === 'hidden') return null;

                  const rotate = position === 'center' ? 0 : position === 'right' ? 6 : -6;
                  const scale = position === 'center' ? 1.08 : 0.92;
                  const translateX = position === 'center' ? 0 : position === 'right' ? 210 : -210;
                  const zIndex = position === 'center' ? 30 : 20;
                  const opacity = position === 'center' ? 1 : 0.88;

                  return (
                    <button
                      type="button"
                      onClick={(e) => {
                        // 如果是滑动操作，不触发点击
                        if (hasSwiped.current) {
                          e.preventDefault();
                          return;
                        }
                        setGameIndex(idx);
                      }}
                      key={`${game.title}-${idx}`}
                      className="group absolute left-1/2 top-4 h-[420px] w-[320px] -translate-x-1/2 overflow-hidden rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-2xl transition duration-300 focus:outline-none"
                      style={{
                        transform: `translate(calc(-50% + ${translateX}px), 0) rotate(${rotate}deg) scale(${scale})`,
                        zIndex,
                        opacity,
                        cursor: position === 'center' ? 'default' : 'pointer',
                      }}
                    >
                      <div className="absolute inset-0 overflow-hidden rounded-3xl">
                        <div
                          className="absolute inset-0 rounded-3xl bg-cover bg-center transition duration-500 group-hover:scale-105"
                          style={{
                            backgroundImage: `url(${game.image || '/images/game-placeholder.jpg'})`,
                          }}
                        />
                        <div
                          className="absolute inset-0 rounded-3xl bg-gradient-to-b from-black/75 via-black/55 to-black/25 opacity-85 transition duration-300 group-hover:opacity-0"
                        />
                      </div>
                      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 blur-2xl opacity-80" />
                      <div className="relative flex items-center justify-between gap-3 text-white">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-200">{t.games.playRecord}</p>
                          <h3 className="mt-1 text-xl font-semibold text-white">{game.title}</h3>
                        </div>
                        <span className="rounded-full bg-white/20 px-3 py-1 text-[12px] font-semibold text-white shadow">
                          {game.hours}
                        </span>
                      </div>
                      <div className="relative mt-3 inline-flex rounded-full bg-white/15 px-3 py-1 text-[12px] font-semibold text-white backdrop-blur">
                        {game.type}
                      </div>
                      <p className="relative mt-3 text-[15px] font-medium leading-7 text-white drop-shadow-md">
                        {game.takeaways}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section id="skills" className="px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-10">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <h2 className="text-3xl font-bold text-slate-900">{t.skills.title}</h2>
              
            </div>
            <div className="grid gap-8 lg:grid-cols-[1.3fr,0.7fr]">
              <div className="space-y-5">
                {skills.map((skill, idx) => {
                  const bubbleStyles = [
                    'bg-gradient-to-r from-indigo-500 to-cyan-400 text-white rotate-[-2deg] shadow-xl',
                    'glass border border-indigo-100 text-slate-800 shadow-lg',
                    'bg-gradient-to-r from-purple-500 via-pink-500 to-amber-400 text-white rotate-1 shadow-xl',
                    'bg-white text-slate-900 shadow-md border border-slate-200',
                    'bg-gradient-to-r from-emerald-400 to-teal-500 text-white rotate-[-1deg] shadow-xl',
                    'glass border border-purple-100 text-slate-800 shadow-lg',
                  ];
                  const sizeStyles = [
                    'rounded-[28px] px-5 py-4',
                    'rounded-[32px] px-6 py-5',
                    'rounded-[24px] px-5 py-3',
                    'rounded-[36px] px-7 py-5',
                  ];
                  const shape = sizeStyles[idx % sizeStyles.length];
                  const color = bubbleStyles[idx % bubbleStyles.length];
                  const align = idx % 2 === 0 ? 'justify-start' : 'justify-end';
                  const isGradient = color.includes('gradient');

                  return (
                    <div
                      key={skill.name}
                      className={`flex items-start gap-3 ${align} transition-transform hover:-translate-y-1`}
                    >
                      <div
                        className={`max-w-[88%] ${shape} ${color}`}
                        style={{
                          borderRadius: idx % 3 === 0 ? '40px 28px 32px 36px' : undefined,
                        }}
                      >
                        <p className="text-base font-semibold">{skill.name}</p>
                        <p className={`${isGradient ? 'text-white/85' : 'text-slate-600'} mt-2 text-sm leading-6`}>
                          {skill.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="glass rounded-2xl p-6 shadow-lg">
                <p className="text-lg font-semibold text-indigo-600">{t.skills.otherSkills}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {otherSkills.map((skill) => (
                    <span
                      key={skill}
                      className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-lg font-medium text-indigo-700"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="portfolio" className="bg-white/70 px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-slate-900">{t.portfolio.title}</h2>
              
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {projects.map((project) => (
                <article
                  key={project.title}
                  className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white/90 shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent opacity-60" />
                  </div>
                  <div className="space-y-3 p-5">
                    <h3 className="text-lg font-semibold text-slate-900">{project.title}</h3>
                    <p className="text-sm leading-6 text-slate-600">{project.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <a
                      href={project.link}
                      target={project.link.startsWith('#') ? '_self' : '_blank'}
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 transition hover:gap-2"
                    >
                      {t.portfolio.viewDetails}
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="gallery" className="px-4 py-16 md:py-20">
          <div className="mx-auto max-w-6xl space-y-10">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-slate-900">{t.gallery.title}</h2>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {mediaItems.map((item) => (
                <div
                  key={item.title}
                  className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white/80 shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
                >
                  {item.type === 'video' ? (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative block h-52 w-full overflow-hidden"
                    >
                      <img
                        src={item.poster}
                        alt={item.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-slate-900/50 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="rounded-full bg-white/90 p-4 shadow-lg transition group-hover:scale-110">
                          <svg className="h-8 w-8 text-slate-900" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                      {item.linkText && (
                        <div className="absolute bottom-2 left-2 right-2 text-center">
                          <span className="inline-block rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                            {item.linkText}
                          </span>
                        </div>
                      )}
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setLightbox({ open: true, src: item.src, type: 'image', title: item.title })}
                      className="relative block h-52 w-full overflow-hidden"
                    >
                      <img
                        src={item.src}
                        alt={item.title}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 transition group-hover:opacity-100" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100">
                        <span className="rounded-full bg-white/80 px-4 py-2 text-sm font-semibold text-slate-900 shadow">
                          {t.gallery.clickToEnlarge}
                        </span>
                      </div>
                    </button>
                  )}
                  <div className="space-y-2 p-4">
                    <p className="text-sm font-semibold text-indigo-600">{item.type === 'video' ? t.gallery.video : t.gallery.image}</p>
                    <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="bg-white/70 px-4 py-16 md:py-20">
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[1fr,1fr]">
            <div className="space-y-4">
              <h2 className="text-3xl font-bold text-slate-900">{t.contact.title}</h2>
              <p className="text-slate-600">
                {t.contact.desc}
              </p>
              <div className="grid gap-3 text-sm">
                {contacts.map((item) => (
                  <div key={item.label} className="flex items-center gap-2 rounded-xl bg-indigo-50/80 px-4 py-3 text-indigo-800">
                    <span className="font-semibold">{item.label}</span>
                    <span className="text-slate-700">·</span>
                    {item.href ? (
                      <a href={item.href} className="underline decoration-indigo-300 underline-offset-4 hover:decoration-indigo-500">
                        {item.value}
                      </a>
                    ) : (
                      <span>{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">{t.contact.leaveMessage}</h3>
              {formStatus.message && (
                <div
                  className={`mt-4 rounded-lg px-4 py-3 text-sm ${
                    formStatus.type === 'success'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {formStatus.message}
                </div>
              )}
              <form className="mt-4 space-y-4" onSubmit={handleFormSubmit}>
                <label className="block space-y-2 text-sm">
                  <span className="text-slate-700">{t.contact.yourName}</span>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full rounded-lg border border-slate-200 bg-white/90 px-3 py-2 outline-none transition focus:border-indigo-400 focus:ring focus:ring-indigo-100"
                  />
                </label>
                <label className="block space-y-2 text-sm">
                  <span className="text-slate-700">{t.contact.yourEmail}</span>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full rounded-lg border border-slate-200 bg-white/90 px-3 py-2 outline-none transition focus:border-indigo-400 focus:ring focus:ring-indigo-100"
                  />
                </label>
                <label className="block space-y-2 text-sm">
                  <span className="text-slate-700">{t.contact.yourMessage}</span>
                  <textarea
                    name="message"
                    rows="4"
                    required
                    className="w-full rounded-lg border border-slate-200 bg-white/90 px-3 py-2 outline-none transition focus:border-indigo-400 focus:ring focus:ring-indigo-100"
                  />
                </label>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 text-white font-semibold shadow-lg shadow-indigo-200 transition hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {isSubmitting ? t.contact.sending : t.contact.sendMessage}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer className="px-4 pb-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 rounded-2xl bg-white px-6 py-6 text-slate-900 shadow-lg md:flex-row">
          <p className="text-sm">&copy; 2024 {language === 'zh' ? '马君羽' : 'Junyu Ma'} · {t.footer.copyright}</p>
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span className="rounded-full bg-slate-200 px-3 py-1 text-slate-800">UE / C++ / React</span>
            {t.footer.tags.map((tag, idx) => (
              <span key={idx} className="rounded-full bg-slate-200 px-3 py-1 text-slate-800">{tag}</span>
            ))}
          </div>
        </div>
      </footer>

      {lightbox.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
          onClick={() => setLightbox({ open: false, src: '', type: 'image', title: '' })}
        >
          <div
            className="relative max-h-[90vh] max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute right-4 top-4 z-10 rounded-full bg-white/90 p-2 text-slate-600 shadow hover:bg-white"
              onClick={() => setLightbox({ open: false, src: '', type: 'image', title: '' })}
              aria-label={t.lightbox.close}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
            {lightbox.type === 'image' ? (
              <img src={lightbox.src} alt={lightbox.title} className="h-full w-full max-h-[90vh] object-contain" />
            ) : (
              <video src={lightbox.src} controls className="h-full w-full max-h-[90vh] object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;

